import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useAnimation, Variants } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Key, ChevronLeft, Plane, ChevronRight, Palette } from 'lucide-react';
import Slider, { Settings, CustomArrowProps } from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import '@/components/sections/VentureTiles.scss';
import realestate from '@/assets/real-estate.jpeg';
import fineart from '@/assets/art.jpeg';
import aviation from '@/assets/aviation.jpeg';
import useScreenSize from '@/hooks/useScreenSize';

type LucideIcon = React.ComponentType<{ size: number } & React.SVGProps<SVGSVGElement>>;

type TileData = {
  id: number;
  category: string;
  title: string;
  description: string;
  imageSrc: string;
  buttonText: string;
  icon: LucideIcon;
  link: string;
};

const TILES_DATA: TileData[] = [
  {
    id: 1,
    category: 'International Real Estate & Hospitality',
    title: 'TRAVEL',
    description:
      "Ephemera seeks out unique property assets in iconic neighborhoods at the heart of the world's great cities. We invest sensitively in collaboration with local architects and designers, then open the reimagined spaces to select guests for exceptional individually curated hospitality experiences.",
    imageSrc: realestate,
    buttonText: 'View',
    icon: Key,
    link: '/ventures/real-estate',
  },
  {
    id: 2,
    category: 'Global Aviation',
    title: 'FLIGHT',
    description:
      'Ephemera invests in structured positions that provide economically leveraged exposure to cash flows generated by the latest technology aviation assets. Our principal brings decades of institutional expertise in aviation leasing and structured finance - capital and knowledge deployed in concert.',
    imageSrc: aviation,
    buttonText: 'Explore',
    icon: Plane,
    link: '/ventures/aviation',
  },
  {
    id: 3,
    category: 'The Ephemera Foundation',
    title: 'Culture',
    description:
      'The same conviction that shapes our investments - that authentic experience rooted in the irreplaceable commands enduring value - finds parallel expression in the Ephemera Foundation. Established by our principals as a nonprofit to support unique human artistic insight in a world flooded with artificial imagery, the Foundation showcases authentic creative expression in partnership with vital cultural institutions and individual artists.',
    imageSrc: fineart,
    buttonText: 'Discover',
    icon: Palette,
    link: '/ventures/ephemera-foundation',
  },
];

const contentStagger: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15 } },
};

export const textEntry: Variants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.6, ease: [0.6, 0.01, 0, 0.95] },
  },
};

const imageContainer: Variants = {
  hidden: { y: '100%' },
  visible: { y: 0, transition: { duration: 0.8, ease: [0.4, 0, 0.2, 1] } },
};

const imageReveal: Variants = {
  hidden: { scaleY: 1 },
  visible: {
    scaleY: 0,
    transition: { duration: 1.2, delay: 0.2, ease: [0.6, 0.01, 0, 0.95] },
  },
};

const PrevArrow = (props: CustomArrowProps) => (
  <button className="venture-tiles__nav-btn venture-tiles__nav-btn--prev" onClick={props.onClick}>
    <ChevronLeft size={24} />
  </button>
);

const NextArrow = (props: CustomArrowProps) => (
  <button className="venture-tiles__nav-btn venture-tiles__nav-btn--next" onClick={props.onClick}>
    <ChevronRight size={24} />
  </button>
);

export const AnimatedButton: React.FC<{ text: string; icon: LucideIcon; onClick: () => void }> = ({
  text,
  icon: Icon,
  onClick,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.button
      className="venture-tiles__btn"
      onClick={onClick}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      <div className="venture-tiles__btn-text">
        {text}
        <motion.span
          className="venture-tiles__btn-underline"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: isHovered ? 1 : 0 }}
          transition={{ duration: 0.4 }}
        />
      </div>
      <motion.div
        className="venture-tiles__btn-icon"
        initial={{ backgroundColor: 'transparent' }}
        animate={{ backgroundColor: isHovered ? '#1D2B36' : 'transparent' }}
        transition={{ duration: 0.3 }}
      >
        <Icon size={16} />
      </motion.div>
    </motion.button>
  );
};

const Tile: React.FC<{ data: TileData }> = ({ data }) => {
  const navigate = useNavigate();
  const { isDesktop } = useScreenSize();
  const controls = useAnimation();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.3 });

  useEffect(() => {
    if (inView) controls.start('visible');
  }, [controls, inView]);

  return (
    <motion.div
      ref={ref}
      className="venture-tiles__item"
      onClick={() => navigate(data.link)}
      initial={{ opacity: 1 }}
    >
      <motion.div
        className="venture-tiles__card"
        initial={{
          y: 0,
          boxShadow: isDesktop
            ? 'inset 0px 0px 0px 1px rgba(70, 70, 70, 0.3), 0px 0px 0px 1px rgba(255, 255, 255, 0.6)'
            : 'none',
        }}
        whileHover={
          isDesktop
            ? {
                y: 3,
                boxShadow: `-2px 2px 0px 0px rgba(255, 255, 255, 0.9), 2px 2px 0px 0px rgba(255, 255, 255, 0.9), 0px 2px 0px 0px rgba(255, 255, 255, 0.9)`,
              }
            : {}
        }
        transition={{ duration: 0.3 }}
      >
        <div className="venture-tiles__img-area">
          <motion.div
            className="venture-tiles__img-wrap"
            initial="hidden"
            animate={controls}
            variants={imageContainer}
          >
            <img src={data.imageSrc} alt={data.category} />
            <motion.div
              className="venture-tiles__img-overlay"
              initial="hidden"
              animate={controls}
              variants={imageReveal}
            />
          </motion.div>
        </div>
        <motion.div
          className="venture-tiles__body"
          initial="hidden"
          animate={controls}
          variants={contentStagger}
        >
          <motion.div className="venture-tiles__header" variants={textEntry}>
            <h3 className="venture-tiles__title">{data.category}</h3>
          </motion.div>
          <motion.p className="venture-tiles__description" variants={textEntry}>
            {data.description}
          </motion.p>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

const DesktopSlider: React.FC<{ tiles: TileData[] }> = ({ tiles }) => {
  const settings: Settings = {
    slidesToShow: 3,
    slidesToScroll: 3,
    dots: false,
    infinite: true,
    speed: 500,
    autoplay: false,
    arrows: false,
  };

  return (
    <Slider {...settings}>
      {tiles.map(data => <Tile key={data.id} data={data} />)}
    </Slider>
  );
};

const MobileTabletSlider: React.FC<{ tiles: TileData[] }> = ({ tiles }) => {
  const settings: Settings = {
    slidesToShow: 1,
    slidesToScroll: 1,
    dots: true,
    infinite: true,
    speed: 500,
    autoplay: true,
    autoplaySpeed: 5000,
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    appendDots: dots => (
      <div className="venture-tiles__dots">
        <ul>{dots}</ul>
      </div>
    ),
  };

  return (
    <Slider {...settings}>
      {tiles.map(data => <Tile key={data.id} data={data} />)}
    </Slider>
  );
};

const VentureTiles: React.FC = () => {
  const [isMounted, setIsMounted] = useState(false);
  const { isDesktop } = useScreenSize();

  useEffect(() => setIsMounted(true), []);

  return (
    <motion.section
      id="ventures"
      className="venture-tiles"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
    >
      <div className="venture-tiles__container">
        {isMounted &&
          (isDesktop ? (
            <DesktopSlider tiles={TILES_DATA} />
          ) : (
            <MobileTabletSlider tiles={TILES_DATA} />
          ))}
      </div>
    </motion.section>
  );
};

export default VentureTiles;
